import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RiLoopLeftLine } from '@remixicon/react';
import FilterDropdown from '../../molecules/FilterDropdown';
import TextArea from '../../atoms/TextArea';
import Input from '../../atoms/Input';
import Button from '../../atoms/Button';
import styles from './ImageGeneration.module.css';
import { Option } from '../../../types/SelectInput';
import {
  generatePrompt,
  generateImage,
} from '../../../services/ImageGeneration';
import { httpClient, HTTPClientResponse } from '../../../utils/httpClient';

// Static options for dropdowns
const storyOptions: Option[] = [
  { id: 'Macro', name: 'Macro', label: 'Macro' },
  { id: 'Micro', name: 'Micro', label: 'Micro' },
];

const subjectOptions: Option[] = [
  { id: 'People', name: 'People', label: 'People' },
  { id: 'Location', name: 'Location', label: 'Location' },
  { id: 'Still Life', name: 'Still Life', label: 'Still Life' },
  { id: 'Metaphors', name: 'Metaphors', label: 'Metaphors' },
];

const stylisticOptions: Option[] = [
  { id: 'Daring', name: 'Daring', label: 'Daring' },
  { id: 'Focused', name: 'Focused', label: 'Focused' },
  { id: 'Compassionate', name: 'Compassionate', label: 'Compassionate' },
  { id: 'Spirited', name: 'Spirited', label: 'Spirited' },
];

const peopleSubOptions: Option[] = [
  { id: 'Lifestyle', name: 'Lifestyle', label: 'Lifestyle' },
  {
    id: 'Studio Portraits',
    name: 'Studio Portraits',
    label: 'Studio Portraits',
  },
  {
    id: 'Pfizer Colleagues',
    name: 'Pfizer Colleagues',
    label: 'Pfizer Colleagues',
  },
  {
    id: 'Doctor and Patients',
    name: 'Doctor and Patients',
    label: 'Doctor and Patients',
  },
];

const locationSubOptions: Option[] = [
  { id: 'Manufacturing', name: 'Manufacturing', label: 'Manufacturing' },
  { id: 'Labs', name: 'Labs', label: 'Labs' },
  { id: 'Global', name: 'Global', label: 'Global' },
];

type ImageGenerationFormData = {
  story: string[];
  subject: string[];
  subSubject: string[];
  style: string[];
  additionalInfo: string;
};

const defaultValues: ImageGenerationFormData = {
  story: [],
  subject: [],
  subSubject: [],
  style: [],
  additionalInfo: '',
};

const getShouldEnableSearch = (options: Option[]) => options.length > 10;

const ImageGenerationFields: React.FC = () => {
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [showImage, setShowImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState('');
  const [showPeopleSubDropdown, setShowPeopleSubDropdown] = useState(false);
  const [showLocationSubDropdown, setShowLocationSubDropdown] = useState(false);
  const [promptLimitWarning, setPromptLimitWarning] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    isRateLimited: boolean;
    message: string;
    retryAfter: number;
  } | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'error' | 'warning' | 'info';
  } | null>(null);

  // Effect to handle countdown timer for rate limiting
  useEffect(() => {
    if (rateLimitInfo?.isRateLimited) {
      const interval = setInterval(() => {
        setRateLimitInfo(prev => {
          if (!prev || prev.retryAfter <= 1) {
            clearInterval(interval);
            setNotification(null);
            return null;
          }
          return {
            ...prev,
            retryAfter: prev.retryAfter - 1,
          };
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [rateLimitInfo?.isRateLimited]);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ImageGenerationFormData>({
      defaultValues,
      mode: 'onChange',
    });

  const selectedSubject = watch('subject');

  useEffect(() => {
    const isPeopleSelected = selectedSubject?.includes('People');
    const isLocationSelected = selectedSubject?.includes('Location');

    setShowPeopleSubDropdown(isPeopleSelected);
    setShowLocationSubDropdown(isLocationSelected);

    if (!isPeopleSelected && !isLocationSelected) {
      setValue('subSubject', []);
    }
  }, [selectedSubject, setValue]);

  const onGeneratePrompt = async (data: ImageGenerationFormData) => {
    setIsLoading(true);
    setError('');
    setShowImage(false);

    try {
      console.log('🔍 FORM DATA RECEIVED:', JSON.stringify(data, null, 2));

      const subject = data.subject[0] || '';
      const subSubject = data.subSubject[0] || '';

      const combinedSubject = subSubject
        ? `${subject} + ${subSubject}`
        : subject;

      const requestBody = {
        story: data.story[0] || '',
        subject: combinedSubject,
        style: data.style[0] || '',
        purpose: data.additionalInfo || '',
      };

      console.log(
        '🚀 SIMPLIFIED REQUEST BODY:',
        JSON.stringify(requestBody, null, 2)
      );
      console.log('🔍 SUBJECT COMBINATION:', {
        subject,
        subSubject,
        combinedSubject,
      });

      const result = await generatePrompt(requestBody);

      console.log(
        '📥 PROMPT GENERATION RESULT:',
        JSON.stringify(result, null, 2)
      );

      if (result.success && result.data?.prompt) {
        console.log(
          '✅ SETTING PROMPT IN UI:',
          result.data.prompt.substring(0, 100) + '...'
        );
        setGeneratedPrompt(result.data.prompt);
      } else {
        console.log('❌ PROMPT GENERATION FAILED:', result.message);
        throw new Error(result.message || 'Failed to generate prompt');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to generate prompt';
      console.error('❌ PROMPT GENERATION ERROR IN UI:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onReset = () => {
    reset(defaultValues);
    setGeneratedPrompt('');
    setImageUrl('');
    setShowImage(false);
    setError('');
    setImageError('');
    setPromptLimitWarning('');
    setRateLimitInfo(null);
    setNotification(null);
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const charCount = newValue.length;

    if (charCount > 1024) {
      setPromptLimitWarning(
        'Text limit exceeded. You have reached the maximum of 1024 characters'
      );
      // Don't update the prompt if it exceeds the limit
      return;
    } else {
      setPromptLimitWarning('');
    }

    setGeneratedPrompt(newValue);
  };

  const onGenerateImage = async () => {
    // Check if currently rate limited
    if (rateLimitInfo?.isRateLimited) {
      setNotification({
        message: rateLimitInfo.message,
        type: 'warning',
      });
      return;
    }

    setIsImageLoading(true);
    setImageError('');
    setNotification(null);

    try {
      const requestBody = {
        prompt: generatedPrompt,
      };

      const result = await generateImage(requestBody);

      // Handle rate limiting specifically (can be 200 OK with success: false or 429)
      if (
        !result.success &&
        (result.statusCode === 429 ||
          result.message?.toLowerCase().includes('rate limit') ||
          result.message?.toLowerCase().includes('adobe firefly') ||
          result.message?.toLowerCase().includes('4 image generation requests'))
      ) {
        const retryMatch = result.message?.match(/(\d+)\s+seconds?/);
        const retryAfter = retryMatch ? parseInt(retryMatch[1]) : 60;

        setRateLimitInfo({
          isRateLimited: true,
          message:
            result.message || 'Rate limit exceeded. Please try again later.',
          retryAfter,
        });

        setNotification({
          message:
            result.message || 'Rate limit exceeded. Please try again later.',
          type: 'warning',
        });

        // Auto-clear rate limit after specified time
        setTimeout(() => {
          setRateLimitInfo(null);
          setNotification(null);
        }, retryAfter * 1000);

        return;
      }

      if (result.success && result.data?.fireflyResponse) {
        const fireflyResponse = result.data.fireflyResponse;
        let generatedImageUrl = '';

        // Check if fireflyResponse is a direct URL
        if (
          typeof fireflyResponse === 'string' &&
          (fireflyResponse.startsWith('http://') ||
            fireflyResponse.startsWith('https://'))
        ) {
          generatedImageUrl = fireflyResponse;
        } else if (typeof fireflyResponse === 'string') {
          // It's a JSON string, parse it
          try {
            const fireflyData = JSON.parse(fireflyResponse);
            generatedImageUrl = fireflyData.outputs?.[0]?.image?.url || '';
          } catch {
            throw new Error('Failed to parse image response');
          }
        }

        if (generatedImageUrl) {
          setImageUrl(generatedImageUrl);
          setShowImage(true);
          // Clear any previous notifications on success
          setNotification(null);
        } else {
          throw new Error('No image URL received from API');
        }
      } else {
        throw new Error(result.message || 'Failed to generate image');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to generate image';
      setImageError(errorMessage);
      // Could also use Logger here for consistency
      // Logger.error(new Error(errorMessage));
    } finally {
      setIsImageLoading(false);
    }
  };

  const onDownloadImage = async () => {
    if (!imageUrl) return;

    try {
      // Fetch the image using httpClient
      const response = (await httpClient.get<Blob>(
        imageUrl,
        {},
        {},
        'blob',
        {},
        true
      )) as HTTPClientResponse;

      if (!response.data) throw new Error('Failed to fetch image');

      const blob = response.data as Blob;

      // Determine file extension from blob type or default to jpg
      let extension = 'jpg';
      if (blob.type === 'image/png') {
        extension = 'png';
      } else if (blob.type === 'image/jpeg' || blob.type === 'image/jpg') {
        extension = 'jpg';
      } else if (blob.type === 'image/webp') {
        extension = 'webp';
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-image-${Date.now()}.${extension}`;
      link.setAttribute(
        'download',
        `generated-image-${Date.now()}.${extension}`
      );
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch {
      // If direct download fails (CORS issue), fallback to opening in new tab
      setImageError(
        'Direct download failed. Opening image in new tab instead...'
      );
      setTimeout(() => {
        window.open(imageUrl, '_blank');
        setImageError('');
      }, 1500);
    }
  };

  const onOpenImageInNewTab = () => {
    if (!imageUrl) return;
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={styles.container}
      onClick={e => e.stopPropagation()}
      role='presentation'
    >
      <form onSubmit={handleSubmit(data => onGeneratePrompt(data))}>
        <div className={styles.formSection}>
          <div className={styles.header}>
            <h2 className={styles.title}>What do you want an image of?</h2>
            <a
              href='https://publicisgroupe.sharepoint.com/sites/CoLabKnowledgeHub/SitePages/GenAI.aspx'
              target='_blank'
              rel='noopener noreferrer'
              className={styles.helpButton}
            >
              Help?
            </a>
          </div>

          <div className={styles.inputField}>
            <Controller
              name='additionalInfo'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  type='text'
                  placeholder='Purpose e.g. - older woman sitting on porch'
                  className={styles.textInput}
                  {...field}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    e.stopPropagation();
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                />
              )}
            />
          </div>

          <div className={styles.dropdownRow}>
            <div className={styles.dropdownField}>
              <Controller
                name='story'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FilterDropdown
                    name='story'
                    list={storyOptions}
                    inputPlaceholder='Story'
                    value={field.value || []}
                    handleSelection={value =>
                      field.onChange(Array.isArray(value) ? value : [value])
                    }
                    required
                    multiSelect={false}
                    className={styles.filterDropdown}
                    inputClassName={styles.inputDropdown}
                  />
                )}
              />
            </div>

            <div className={styles.dropdownField}>
              <Controller
                name='subject'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FilterDropdown
                    name='subject'
                    list={subjectOptions}
                    inputPlaceholder='Subject'
                    value={field.value || []}
                    handleSelection={value =>
                      field.onChange(Array.isArray(value) ? value : [value])
                    }
                    required
                    multiSelect={false}
                    className={styles.filterDropdown}
                    inputClassName={styles.inputDropdown}
                    isSearchable={getShouldEnableSearch(subjectOptions)}
                  />
                )}
              />
            </div>

            <div className={styles.dropdownField}>
              <Controller
                name='style'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FilterDropdown
                    name='style'
                    list={stylisticOptions}
                    inputPlaceholder='Style'
                    value={field.value || []}
                    handleSelection={value =>
                      field.onChange(Array.isArray(value) ? value : [value])
                    }
                    required
                    multiSelect={false}
                    className={styles.filterDropdown}
                    inputClassName={styles.inputDropdown}
                    isSearchable={getShouldEnableSearch(stylisticOptions)}
                  />
                )}
              />
            </div>
          </div>

          {showPeopleSubDropdown && (
            <div className={styles.subDropdownRow}>
              <div className={styles.dropdownField}>
                <Controller
                  name='subSubject'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FilterDropdown
                      name='subSubject'
                      list={peopleSubOptions}
                      inputPlaceholder='Select People Type'
                      value={field.value || []}
                      handleSelection={value =>
                        field.onChange(Array.isArray(value) ? value : [value])
                      }
                      required
                      multiSelect={false}
                      className={styles.filterDropdown}
                      inputClassName={styles.inputDropdown}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {showLocationSubDropdown && (
            <div className={styles.subDropdownRow}>
              <div className={styles.dropdownField}>
                <Controller
                  name='subSubject'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FilterDropdown
                      name='subSubject'
                      list={locationSubOptions}
                      inputPlaceholder='Select Location Type'
                      value={field.value || []}
                      handleSelection={value =>
                        field.onChange(Array.isArray(value) ? value : [value])
                      }
                      required
                      multiSelect={false}
                      className={styles.filterDropdown}
                      inputClassName={styles.inputDropdown}
                      isSearchable={getShouldEnableSearch(locationSubOptions)}
                    />
                  )}
                />
              </div>
            </div>
          )}

          <div className={styles.buttonRow}>
            <Button
              type='submit'
              colorScheme='primary'
              variant='solid'
              size='lg'
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Generate Prompt'}
            </Button>

            {generatedPrompt && (
              <Button
                type='button'
                colorScheme='primary'
                variant='outline'
                size='lg'
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReset();
                }}
                disabled={isLoading}
              >
                <RiLoopLeftLine size={20} />
                Reset
              </Button>
            )}
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>

        {generatedPrompt && (
          <div className={styles.promptSection}>
            <div className={styles.promptHeader}>
              <h3 className={styles.promptTitle}>Generated Prompt</h3>
            </div>

            <div className={styles.promptWrapper}>
              <div className={styles.promptContent}>
                <TextArea
                  value={generatedPrompt}
                  handleOnChange={handlePromptChange}
                  classes={styles.promptTextarea}
                  hasError={false}
                  rows={8}
                  readOnly={false}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    e.stopPropagation();
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                />
                {promptLimitWarning && (
                  <div className={styles.warningMessage}>
                    {promptLimitWarning}
                  </div>
                )}
              </div>

              {showImage && imageUrl && (
                <div className={styles.imageColumn}>
                  <div className={styles.imagePreview}>
                    <img
                      src={imageUrl}
                      alt='Generated'
                      className={styles.generatedImage}
                    />
                  </div>
                  <p className={styles.imageCaption}>
                    This image is generated by using{' '}
                    <a
                      href='https://www.adobe.com/products/firefly.html'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Adobe Firefly
                    </a>
                  </p>
                </div>
              )}
            </div>

            <div className={styles.imageButtonRow}>
              <Button
                type='button'
                colorScheme='primary'
                variant='solid'
                size='lg'
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onGenerateImage();
                }}
                disabled={isImageLoading || rateLimitInfo?.isRateLimited}
              >
                {isImageLoading ? 'Generating Image...' : 'Generate Image'}
              </Button>
              {showImage && imageUrl && (
                <>
                  <Button
                    type='button'
                    colorScheme='primary'
                    variant='solid'
                    size='lg'
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDownloadImage();
                    }}
                  >
                    Download Image
                  </Button>
                  <Button
                    type='button'
                    colorScheme='primary'
                    variant='outline'
                    size='lg'
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenImageInNewTab();
                    }}
                  >
                    Open Image
                  </Button>
                </>
              )}
              <Button
                type='button'
                colorScheme='primary'
                variant='outline'
                size='lg'
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReset();
                }}
                disabled={isImageLoading}
              >
                <RiLoopLeftLine size={20} />
                Reset
              </Button>
            </div>
            {notification && (
              <div
                className={`${styles.notificationMessage} ${styles[`notification--${notification.type}`]}`}
              >
                {notification.message}
              </div>
            )}
            {imageError && (
              <div className={styles.errorMessage}>{imageError}</div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default ImageGenerationFields;
